/**
 * @fileoverview Design Tokens Section - Component Library
 *
 * Purpose: Display spacing, radius, grid, and other design tokens
 * Used by: Component library main page
 */

export function DesignTokensSection() {
  return (
    <div id="design-tokens" className="scroll-mt-24 space-y-12">
      <div>
        <h1 className="mb-2 text-4xl font-bold tracking-tight">
          Design Tokens
        </h1>
        <p className="text-lg text-muted-foreground">
          Spacing scale, border radius, grid systems, and layout utilities.
        </p>
      </div>

      {/* Spacing Scale */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Spacing Scale
          </h2>
          <p className="text-muted-foreground">
            Consistent spacing values used for padding, margin, and gaps
            throughout the design system.
          </p>
        </div>
        <div className="rounded-lg border bg-card p-8">
          <div className="space-y-4">
            {[
              { size: "0", value: "0px", rem: "0rem" },
              { size: "0.5", value: "2px", rem: "0.125rem" },
              { size: "1", value: "4px", rem: "0.25rem" },
              { size: "1.5", value: "6px", rem: "0.375rem" },
              { size: "2", value: "8px", rem: "0.5rem" },
              { size: "2.5", value: "10px", rem: "0.625rem" },
              { size: "3", value: "12px", rem: "0.75rem" },
              { size: "3.5", value: "14px", rem: "0.875rem" },
              { size: "4", value: "16px", rem: "1rem" },
              { size: "5", value: "20px", rem: "1.25rem" },
              { size: "6", value: "24px", rem: "1.5rem" },
              { size: "7", value: "28px", rem: "1.75rem" },
              { size: "8", value: "32px", rem: "2rem" },
              { size: "9", value: "36px", rem: "2.25rem" },
              { size: "10", value: "40px", rem: "2.5rem" },
              { size: "12", value: "48px", rem: "3rem" },
              { size: "16", value: "64px", rem: "4rem" },
              { size: "20", value: "80px", rem: "5rem" },
              { size: "24", value: "96px", rem: "6rem" },
            ].map((space) => (
              <div key={space.size} className="flex items-center gap-6">
                <div className="w-16 font-mono text-sm">{space.size}</div>
                <div className="flex flex-1 items-center gap-4">
                  <div
                    className="bg-primary"
                    style={{ width: space.value, height: "20px" }}
                  ></div>
                  <div className="text-sm text-muted-foreground">
                    {space.value} ({space.rem})
                  </div>
                </div>
                <code className="font-mono text-xs text-muted-foreground">
                  p-{space.size} / m-{space.size} / gap-{space.size}
                </code>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Spacing Patterns */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Common Spacing Patterns
          </h2>
          <p className="text-muted-foreground">
            Frequently used spacing combinations in the design system.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4 rounded-lg border bg-card p-8">
            <h3 className="text-lg font-semibold">Cards & Containers</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span>Small padding</span>
                <code className="rounded bg-muted px-2 py-1 text-xs">p-4</code>
              </div>
              <div className="flex items-center justify-between">
                <span>Medium padding</span>
                <code className="rounded bg-muted px-2 py-1 text-xs">p-6</code>
              </div>
              <div className="flex items-center justify-between">
                <span>Large padding</span>
                <code className="rounded bg-muted px-2 py-1 text-xs">p-8</code>
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-lg border bg-card p-8">
            <h3 className="text-lg font-semibold">Component Spacing</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span>Tight spacing</span>
                <code className="rounded bg-muted px-2 py-1 text-xs">
                  space-y-2 / gap-2
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span>Normal spacing</span>
                <code className="rounded bg-muted px-2 py-1 text-xs">
                  space-y-4 / gap-4
                </code>
              </div>
              <div className="flex items-center justify-between">
                <span>Loose spacing</span>
                <code className="rounded bg-muted px-2 py-1 text-xs">
                  space-y-6 / gap-6
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Border Radius */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Border Radius
          </h2>
          <p className="text-muted-foreground">
            Border radius values controlled by the{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
              --radius
            </code>{" "}
            CSS variable (default: 0.5rem).
          </p>
        </div>
        <div className="rounded-lg border bg-card p-8">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
            {[
              { name: "None", class: "rounded-none", value: "0" },
              {
                name: "Small",
                class: "rounded-sm",
                value: "calc(var(--radius) - 4px)",
              },
              {
                name: "Medium",
                class: "rounded-md",
                value: "calc(var(--radius) - 2px)",
              },
              { name: "Default", class: "rounded", value: "0.25rem" },
              { name: "Large", class: "rounded-lg", value: "var(--radius)" },
              { name: "Extra Large", class: "rounded-xl", value: "0.75rem" },
              { name: "2XL", class: "rounded-2xl", value: "1rem" },
              { name: "3XL", class: "rounded-3xl", value: "1.5rem" },
              { name: "Full", class: "rounded-full", value: "9999px" },
            ].map((radius) => (
              <div key={radius.name} className="space-y-3">
                <div className={`h-20 w-full bg-primary ${radius.class}`}></div>
                <div>
                  <div className="text-sm font-medium">{radius.name}</div>
                  <code className="font-mono text-xs text-muted-foreground">
                    {radius.class}
                  </code>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {radius.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Grid System */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Grid System
          </h2>
          <p className="text-muted-foreground">
            Responsive grid layouts with Tailwind CSS utilities.
          </p>
        </div>

        <div className="space-y-8">
          {/* 12 Column Grid */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">12-Column Grid</h3>
            <div className="rounded-lg border bg-card p-6">
              <div className="grid grid-cols-12 gap-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex h-12 items-center justify-center rounded border border-primary bg-primary/20 text-xs font-medium"
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
              <code className="mt-3 block font-mono text-xs text-muted-foreground">
                grid grid-cols-12 gap-4
              </code>
            </div>
          </div>

          {/* Responsive Grids */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">
              Responsive Grid (1 → 2 → 3 → 4)
            </h3>
            <div className="rounded-lg border bg-card p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex h-20 items-center justify-center rounded bg-muted text-sm font-medium"
                  >
                    Item {i + 1}
                  </div>
                ))}
              </div>
              <code className="mt-3 block font-mono text-xs text-muted-foreground">
                grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4
                gap-4
              </code>
            </div>
          </div>

          {/* Auto Fit Grid */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">
              Auto-Fit Grid (Responsive Cards)
            </h3>
            <div className="rounded-lg border bg-card p-6">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex h-24 items-center justify-center rounded bg-muted text-sm font-medium"
                  >
                    Card {i + 1}
                  </div>
                ))}
              </div>
              <code className="mt-3 block font-mono text-xs text-muted-foreground">
                grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4
              </code>
            </div>
          </div>
        </div>
      </section>

      {/* Flexbox Patterns */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Flexbox Patterns
          </h2>
          <p className="text-muted-foreground">
            Common flex layouts used throughout the design system.
          </p>
        </div>
        <div className="space-y-6">
          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">Space Between</h3>
            <div className="flex items-center justify-between rounded bg-muted p-4">
              <span className="text-sm">Left Item</span>
              <span className="text-sm">Right Item</span>
            </div>
            <code className="font-mono text-xs text-muted-foreground">
              flex justify-between items-center
            </code>
          </div>

          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">Centered</h3>
            <div className="flex h-24 items-center justify-center rounded bg-muted p-4">
              <span className="text-sm">Centered Content</span>
            </div>
            <code className="font-mono text-xs text-muted-foreground">
              flex justify-center items-center
            </code>
          </div>

          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">Horizontal Stack with Gap</h3>
            <div className="flex items-center gap-4 rounded bg-muted p-4">
              <div className="h-10 w-10 rounded bg-primary"></div>
              <div className="h-10 w-10 rounded bg-primary"></div>
              <div className="h-10 w-10 rounded bg-primary"></div>
              <div className="h-10 w-10 rounded bg-primary"></div>
            </div>
            <code className="font-mono text-xs text-muted-foreground">
              flex items-center gap-4
            </code>
          </div>

          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">Vertical Stack with Space</h3>
            <div className="space-y-4 rounded bg-muted p-4">
              <div className="h-10 rounded bg-primary"></div>
              <div className="h-10 rounded bg-primary"></div>
              <div className="h-10 rounded bg-primary"></div>
            </div>
            <code className="font-mono text-xs text-muted-foreground">
              space-y-4
            </code>
          </div>
        </div>
      </section>

      {/* Container Widths */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Container Widths
          </h2>
          <p className="text-muted-foreground">
            Max-width constraints for content containers.
          </p>
        </div>
        <div className="space-y-4 rounded-lg border bg-card p-8">
          {[
            { name: "Extra Small", class: "max-w-xs", value: "320px" },
            { name: "Small", class: "max-w-sm", value: "384px" },
            { name: "Medium", class: "max-w-md", value: "448px" },
            { name: "Large", class: "max-w-lg", value: "512px" },
            { name: "Extra Large", class: "max-w-xl", value: "576px" },
            { name: "2XL", class: "max-w-2xl", value: "672px" },
            { name: "3XL", class: "max-w-3xl", value: "768px" },
            { name: "4XL", class: "max-w-4xl", value: "896px" },
            { name: "5XL", class: "max-w-5xl", value: "1024px" },
            { name: "6XL", class: "max-w-6xl", value: "1152px" },
            { name: "7XL", class: "max-w-7xl", value: "1280px" },
          ].map((container) => (
            <div key={container.class} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{container.name}</span>
                <code className="font-mono text-xs text-muted-foreground">
                  {container.class} = {container.value}
                </code>
              </div>
              <div
                className={`${container.class} h-8 rounded border-l-4 border-primary bg-primary/20`}
              ></div>
            </div>
          ))}
        </div>
      </section>

      {/* Shadows */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Shadows</h2>
          <p className="text-muted-foreground">
            Box shadow variants for depth and elevation.
          </p>
        </div>
        <div className="rounded-lg border bg-card p-8">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
            {[
              { name: "None", class: "shadow-none" },
              { name: "Small", class: "shadow-sm" },
              { name: "Default", class: "shadow" },
              { name: "Medium", class: "shadow-md" },
              { name: "Large", class: "shadow-lg" },
              { name: "Extra Large", class: "shadow-xl" },
              { name: "2XL", class: "shadow-2xl" },
            ].map((shadow) => (
              <div key={shadow.name} className="space-y-3">
                <div
                  className={`flex h-24 items-center justify-center rounded-lg border bg-card ${shadow.class}`}
                >
                  <span className="text-sm font-medium">{shadow.name}</span>
                </div>
                <code className="block text-center font-mono text-xs text-muted-foreground">
                  {shadow.class}
                </code>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Focus States */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Focus States
          </h2>
          <p className="text-muted-foreground">
            Focus ring styles for keyboard navigation and accessibility. Click
            or tab through elements to see focus states.
          </p>
        </div>
        <div className="space-y-6 rounded-lg border bg-card p-8">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Focus Ring Variants</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <button className="rounded-md border bg-background px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                Default Focus Ring
              </button>
              <button className="rounded-md border bg-background px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                Primary Focus Ring
              </button>
              <button className="rounded-md border bg-background px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2">
                Destructive Focus Ring
              </button>
              <button className="rounded-md border bg-background px-4 py-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                Thin Focus Ring
              </button>
            </div>
            <div className="mt-4 space-y-1 text-xs text-muted-foreground">
              <p>
                <code className="rounded bg-muted px-1.5 py-0.5">
                  focus-visible:ring-2 focus-visible:ring-ring
                </code>{" "}
                - Default (2px ring)
              </p>
              <p>
                <code className="rounded bg-muted px-1.5 py-0.5">
                  focus-visible:ring-offset-2
                </code>{" "}
                - 2px offset for visibility
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Focus Ring Widths</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                { width: "1", label: "Thin" },
                { width: "2", label: "Default" },
                { width: "4", label: "Thick" },
              ].map((focus) => (
                <div key={focus.width} className="space-y-2">
                  <button
                    className={`w-full rounded-md border bg-background px-4 py-2 focus-visible:outline-none focus-visible:ring-${focus.width} focus-visible:ring-ring focus-visible:ring-offset-2`}
                  >
                    {focus.label} Ring
                  </button>
                  <code className="block font-mono text-xs text-muted-foreground">
                    ring-{focus.width}
                  </code>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold">
              Focus on Different Elements
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Input with focus"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
              <div className="flex items-center gap-2">
                <button className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  Button Focus
                </button>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href="#"
                  className="rounded px-1 text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  Link Focus
                </a>
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Try tabbing through these elements to see the focus rings in
              action.
            </p>
          </div>
        </div>
      </section>

      {/* Backdrop Blur */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Backdrop Blur
          </h2>
          <p className="text-muted-foreground">
            Backdrop filter blur effects for frosted glass and overlay
            backgrounds.
          </p>
        </div>
        <div className="rounded-lg border bg-card p-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              { name: "None", class: "backdrop-blur-none", blur: "0" },
              { name: "Small", class: "backdrop-blur-sm", blur: "4px" },
              { name: "Default", class: "backdrop-blur", blur: "8px" },
              { name: "Medium", class: "backdrop-blur-md", blur: "12px" },
              { name: "Large", class: "backdrop-blur-lg", blur: "16px" },
              { name: "Extra Large", class: "backdrop-blur-xl", blur: "24px" },
              { name: "2XL", class: "backdrop-blur-2xl", blur: "40px" },
              { name: "3XL", class: "backdrop-blur-3xl", blur: "64px" },
            ].map((blur) => (
              <div key={blur.name} className="space-y-3">
                <div className="relative h-32 overflow-hidden rounded-lg border">
                  {/* Colorful background with pattern */}
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500"></div>
                    <div
                      className="absolute inset-0 opacity-30"
                      style={{
                        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px),
                                         repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)`,
                      }}
                    ></div>
                    {/* Add some emoji/text for more visual interest */}
                    <div className="absolute inset-0 flex items-center justify-center text-6xl opacity-20">
                      🎨
                    </div>
                  </div>
                  {/* Blurred overlay with text */}
                  <div
                    className={`absolute inset-0 ${blur.class} flex items-center justify-center border-2 border-white/20 bg-background/40`}
                  >
                    <div className="text-center">
                      <div className="text-sm font-semibold text-white drop-shadow-md">
                        {blur.name}
                      </div>
                      <div className="text-xs text-white/90">{blur.blur}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <code className="font-mono text-xs text-muted-foreground">
                    {blur.class}
                  </code>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            💡 Tip: Compare "None" to "3XL" to see the full range of blur
            intensity
          </p>
        </div>
      </section>

      {/* Backdrop Blur Usage */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Backdrop Blur Usage
          </h2>
          <p className="text-muted-foreground">
            Common use cases for backdrop blur effects.
          </p>
        </div>
        <div className="space-y-6">
          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">Sticky Header with Blur</h3>
            <div className="relative h-48 overflow-hidden rounded-lg border">
              <div className="absolute inset-0 space-y-2 overflow-y-auto p-6">
                {Array.from({ length: 10 }).map((_, i) => (
                  <p key={i} className="text-sm">
                    Content line {i + 1} - Scroll to see the header blur effect
                  </p>
                ))}
              </div>
              <div className="sticky top-0 border-b bg-background/80 px-6 py-4 backdrop-blur-md">
                <h4 className="font-semibold">Sticky Header</h4>
              </div>
            </div>
            <code className="block font-mono text-xs text-muted-foreground">
              backdrop-blur-md bg-background/80
            </code>
          </div>

          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">Modal Overlay</h3>
            <div className="relative h-48 overflow-hidden rounded-lg">
              <div
                className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20"
                style={{
                  backgroundImage:
                    'url("https://images.unsplash.com/photo-1557683316-973673baf926?w=400")',
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm">
                <div className="max-w-sm rounded-lg border bg-card p-6 shadow-lg">
                  <h4 className="mb-2 font-semibold">Modal Content</h4>
                  <p className="text-sm text-muted-foreground">
                    Backdrop blur creates a frosted glass effect
                  </p>
                </div>
              </div>
            </div>
            <code className="block font-mono text-xs text-muted-foreground">
              backdrop-blur-sm bg-background/60
            </code>
          </div>

          <div className="space-y-4 rounded-lg border bg-card p-6">
            <h3 className="text-sm font-semibold">
              Navigation with supports-backdrop-filter
            </h3>
            <div className="relative h-32 overflow-hidden rounded-lg border">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/10 p-6">
                <p className="text-sm">Background content</p>
              </div>
              <div className="absolute left-0 right-0 top-0 border-b bg-background/95 px-6 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <span className="text-sm font-medium">Navigation Bar</span>
              </div>
            </div>
            <code className="block font-mono text-xs text-muted-foreground">
              backdrop-blur supports-[backdrop-filter]:bg-background/60
            </code>
            <p className="text-xs text-muted-foreground">
              This pattern is used in the component library header (see top of
              page)
            </p>
          </div>
        </div>
      </section>

      {/* Breakpoints */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Responsive Breakpoints
          </h2>
          <p className="text-muted-foreground">
            Screen size breakpoints for responsive design (mobile-first
            approach).
          </p>
        </div>
        <div className="rounded-lg border bg-card p-8">
          <div className="space-y-4">
            {[
              {
                name: "Default",
                prefix: "(base)",
                size: "< 640px",
                desc: "Mobile phones",
              },
              {
                name: "Small",
                prefix: "sm:",
                size: "≥ 640px",
                desc: "Large phones, small tablets",
              },
              {
                name: "Medium",
                prefix: "md:",
                size: "≥ 768px",
                desc: "Tablets",
              },
              {
                name: "Large",
                prefix: "lg:",
                size: "≥ 1024px",
                desc: "Laptops, desktops",
              },
              {
                name: "Extra Large",
                prefix: "xl:",
                size: "≥ 1280px",
                desc: "Large desktops",
              },
              {
                name: "2XL",
                prefix: "2xl:",
                size: "≥ 1536px",
                desc: "Extra large screens",
              },
            ].map((bp) => (
              <div
                key={bp.name}
                className="flex items-center justify-between rounded bg-muted p-4"
              >
                <div className="flex-1">
                  <div className="font-medium">{bp.name}</div>
                  <div className="text-xs text-muted-foreground">{bp.desc}</div>
                </div>
                <div className="text-right">
                  <code className="font-mono text-sm text-primary">
                    {bp.prefix}
                  </code>
                  <div className="text-xs text-muted-foreground">{bp.size}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Edit */}
      <section className="rounded-lg border bg-card p-8 text-card-foreground">
        <h2 className="mb-4 text-xl font-semibold">
          How to Edit Design Tokens
        </h2>
        <div className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="mb-2 font-medium text-foreground">Spacing & Sizing</p>
            <p>
              Tailwind uses a default spacing scale. To customize, edit{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                tailwind.config.ts
              </code>
              :
            </p>
            <div className="mt-2 overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
              <pre>{`theme: {
  extend: {
    spacing: {
      '128': '32rem',
      '144': '36rem',
    }
  }
}`}</pre>
            </div>
          </div>

          <div>
            <p className="mb-2 font-medium text-foreground">Border Radius</p>
            <p>
              Edit the{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                --radius
              </code>{" "}
              variable in{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                src/index.css
              </code>
              :
            </p>
            <div className="mt-2 rounded-md bg-muted p-3 font-mono text-xs">
              <pre>{`:root {
  --radius: 0.5rem;  /* Change to 0.75rem for rounder corners */
}`}</pre>
            </div>
          </div>

          <div>
            <p className="mb-2 font-medium text-foreground">Breakpoints</p>
            <p>
              Customize breakpoints in{" "}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-foreground">
                tailwind.config.ts
              </code>
              :
            </p>
            <div className="mt-2 overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
              <pre>{`theme: {
  screens: {
    'sm': '640px',
    'md': '768px',
    'lg': '1024px',
    'xl': '1280px',
    '2xl': '1536px',
  }
}`}</pre>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
